import { useEffect, useState } from "react";
import { Menu, ShoppingBag } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { LANGS, useI18n } from "@/i18n";

const LINK_IDS = ["products", "order", "how", "faq", "contacts"];

export const Logo = ({ compact = false }) => (
  <a href="/" className="flex items-center gap-2.5" data-testid="header-logo-link">
    <img src="/logo-mark.png" alt="RPM 3D Print" className="h-10 w-auto" />
    {compact !== true && (
      <span className={`font-display text-[17px] font-bold leading-none tracking-tight ${compact === "auto" ? "hidden sm:inline" : ""}`}>
        RPM <span className="font-medium">3D PRINT</span>
      </span>
    )}
  </a>
);

export const LangSwitch = ({ className = "", testPrefix = "lang" }) => {
  const { lang, setLang, t } = useI18n();
  return (
    <div className={`inline-flex rounded-xl border border-zinc-200 bg-white p-0.5 ${className}`} role="group" aria-label={t("nav.language")}>
      {LANGS.map((l) => (
        <button
          key={l.id}
          type="button"
          onClick={() => setLang(l.id)}
          aria-pressed={lang === l.id}
          className={`h-9 min-w-[38px] rounded-[10px] px-2 font-mono-rpm text-xs font-medium transition-colors duration-200 ${
            lang === l.id ? "bg-black text-white" : "text-zinc-600 hover:bg-zinc-100 hover:text-black"
          }`}
          data-testid={`${testPrefix}-${l.id}-button`}
        >
          {l.label}
        </button>
      ))}
    </div>
  );
};

export const Header = ({ cartCount = 0, minimal = false }) => {
  const { t } = useI18n();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const on = () => setScrolled(window.scrollY > 8);
    on();
    window.addEventListener("scroll", on, { passive: true });
    return () => window.removeEventListener("scroll", on);
  }, []);

  const base = minimal ? "/" : "";

  return (
    <header
      className={`sticky top-0 z-40 bg-white transition-shadow duration-200 ${scrolled ? "shadow-[0_1px_0_#e4e4e7,0_8px_24px_rgba(0,0,0,0.04)]" : "shadow-[0_1px_0_#f4f4f5]"}`}
      data-testid="site-header"
    >
      <div className="mx-auto flex h-[68px] max-w-6xl items-center justify-between gap-2 px-4 sm:px-6">
        <Logo compact="auto" />
        <nav className="hidden items-center gap-1 lg:flex" aria-label="main">
          {LINK_IDS.map((id) => (
            <a
              key={id}
              href={`${base}#${id}`}
              className="rounded-lg px-3 py-2 text-sm font-medium text-zinc-700 transition-colors duration-200 hover:bg-zinc-100 hover:text-black"
              data-testid={`nav-${id}-link`}
            >
              {t(`nav.${id}`)}
            </a>
          ))}
        </nav>
        <div className="flex items-center gap-2">
          <LangSwitch />
          <a href={`${base}#order`} className="rpm-btn rpm-btn-orange !min-h-[42px] !px-3 text-sm sm:!px-4" aria-label={t("nav.buy")} data-testid="header-buy-button">
            <ShoppingBag className="h-4 w-4 shrink-0" />
            <span className="hidden sm:inline">{t("nav.buy")}</span>
            {cartCount > 0 && (
              <span className="ml-0.5 rounded-full bg-black px-2 py-0.5 text-xs text-white" data-testid="header-cart-count">
                {cartCount}
              </span>
            )}
          </a>
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <button
                className="flex h-[42px] w-[42px] items-center justify-center rounded-xl border border-zinc-200 bg-white lg:hidden"
                aria-label={t("nav.openMenu")}
                data-testid="mobile-menu-button"
              >
                <Menu className="h-5 w-5" />
              </button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[300px] bg-white">
              <SheetHeader>
                <SheetTitle className="text-left">
                  <Logo />
                </SheetTitle>
              </SheetHeader>
              <LangSwitch className="mt-6" testPrefix="mobile-lang" />
              <nav className="mt-4 flex flex-col gap-1">
                {LINK_IDS.map((id) => (
                  <a
                    key={id}
                    href={`${base}#${id}`}
                    onClick={() => setOpen(false)}
                    className="rounded-xl px-3 py-3 font-display text-lg font-medium hover:bg-zinc-100"
                    data-testid={`mobile-nav-${id}-link`}
                  >
                    {t(`nav.${id}`)}
                  </a>
                ))}
              </nav>
              <a href={`${base}#order`} onClick={() => setOpen(false)} className="rpm-btn rpm-btn-orange mt-6 w-full" data-testid="mobile-menu-buy-button">
                {t("nav.build")}
              </a>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
};
